// Dr.Rx™ System Ingestion & Fallback Controls
import React, { useState, useEffect } from 'react';
import { getNationalAlertsMetadata } from '../../services/alertService';
import { triggerNationalAlertsUpdate, getAlertsConfig, setAlertsConfig } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';

const JobRow: React.FC<{
    name: string;
    schedule: string;
    lastRun: string;
    onTrigger: () => void;
    loading: boolean;
}> = ({ name, schedule, lastRun, onTrigger, loading }) => (
    <div className="bg-[#1E2E4A] p-4 rounded-xl border border-blue-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
            <h4 className="font-bold text-white text-sm">{name}</h4>
            <div className="flex flex-wrap gap-4 text-xs text-gray-400 mt-1 font-mono tabular-nums">
                <span>SCHEDULE: {schedule}</span>
                <span className="text-blue-300">LAST RUN: {lastRun}</span>
            </div>
        </div>
        <button
            onClick={onTrigger}
            disabled={loading}
            className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-all self-start sm:self-auto ${
                loading
                    ? 'bg-gray-700 text-gray-500 cursor-wait'
                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/50'
            }`}
        >
            {loading ? 'Executing...' : 'Trigger Now'}
        </button>
    </div>
);

const SystemControls: React.FC = () => {
    const { showToast } = useToast();
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [loadingNat, setLoadingNat] = useState(false);
    const [fallbackActive, setFallbackActive] = useState(false);

    const refresh = async () => {
        const { lastUpdated: t } = await getNationalAlertsMetadata();
        setLastUpdated(t);
        const cfg = await getAlertsConfig();
        setFallbackActive(cfg.isFrontendFallbackActive);
    };

    useEffect(() => {
        refresh();
    }, []);

    const runNat = async () => {
        setLoadingNat(true);
        try {
            await triggerNationalAlertsUpdate();
            await refresh();
            showToast('National alerts ingestion job completed.', 'success');
        } catch (e: any) {
            showToast(`National job failed: ${e.message}`, 'error');
        } finally {
            setLoadingNat(false);
        }
    };

    const toggleFallback = async () => {
        const next = !fallbackActive;
        await setAlertsConfig({ isFrontendFallbackActive: next });
        setFallbackActive(next);
        showToast(
            `Emergency Fallback Mode ${next ? 'activated' : 'deactivated'}.`,
            next ? 'warning' : 'success'
        );
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-2">Background Cloud Jobs</h3>
                <JobRow
                    name="National Regulatory Scraper (FDA / CDC / DEA)"
                    schedule="Every 24h (05:00 EST)"
                    lastRun={lastUpdated ? lastUpdated.toLocaleString() : 'Unknown'}
                    onTrigger={runNat}
                    loading={loadingNat}
                />
            </div>

            <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-2">Emergency Circuit Breakers</h3>
                <div className="bg-red-900/10 border border-red-500/30 p-6 rounded-xl flex items-center justify-between gap-4">
                    <div>
                        <h4 className="font-bold text-red-200 text-base">Force Frontend Fallback Mode</h4>
                        <p className="text-xs text-red-300/70 mt-1 max-w-sm">
                            Overrides live database queries for all users, forcing the app to use bundled static alert data. Use only during database outages.
                        </p>
                    </div>
                    <button
                        onClick={toggleFallback}
                        className={`w-14 h-8 rounded-full p-1 transition-colors flex-shrink-0 ${
                            fallbackActive ? 'bg-red-500' : 'bg-gray-700'
                        }`}
                        aria-label="Toggle Force Frontend Fallback Mode"
                    >
                        <div
                            className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                                fallbackActive ? 'translate-x-6' : 'translate-x-0'
                            }`}
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SystemControls;
